import { useState, useEffect, useCallback } from 'react'; // Removed useRef
import {
  VolumeDiscount,
  VolumeDiscountListResponse,
  VolumeDiscountCreate,
  VolumeDiscountUpdate,
} from '../types/costwand';
import { apiClient, ApiError } from '../api/apiClient.ts';

const PAGE_SIZE = 10;

interface UseVolumeDiscountResult {
  volumeDiscounts: VolumeDiscount[];
  loading: boolean;
  error: string | null;
  currentPage: number;
  totalPages: number;
  totalItems: number;
  fetchVolumeDiscounts: (page?: number) => Promise<void>;
  handlePageChange: (page: number) => void;
  createVolumeDiscount: (data: VolumeDiscountCreate) => Promise<boolean>;
  updateVolumeDiscount: (id: number, data: VolumeDiscountUpdate) => Promise<boolean>;
  deleteVolumeDiscount: (id: number) => Promise<boolean>;
}

export const useVolumeDiscount = (): UseVolumeDiscountResult => {
  const [volumeDiscounts, setVolumeDiscounts] = useState<VolumeDiscount[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Removed hasFetchedInitial useRef

  const fetchVolumeDiscounts = useCallback(async (page: number = currentPage) => {
    if (page < 1) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      // This is a paginated hook, so no allPages: true
      const response = await apiClient<VolumeDiscountListResponse>(
        `/discounts?page=${page}&page_size=${PAGE_SIZE}`,
        { bypassCache: true } // MODIFIED: Added bypassCache for initial fetch
      );
      setVolumeDiscounts(response.data);
      setTotalPages(response.pagination.total_pages);
      setTotalItems(response.pagination.total_items);
      setCurrentPage(response.pagination.page);


    } catch (err) {
      if (err instanceof ApiError) {
        setError(`Failed to fetch Volume Discounts: ${err.message}`);
      } else {
        setError('An unknown error occurred while fetching Volume Discounts.');
      }
      setVolumeDiscounts([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  }, [currentPage]);

  useEffect(() => {
    // Simplified useEffect: always fetch when currentPage changes
    fetchVolumeDiscounts(currentPage);
  }, [currentPage, fetchVolumeDiscounts]);

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const createVolumeDiscount = async (data: VolumeDiscountCreate): Promise<boolean> => {
    setError(null);
    try {
      await apiClient<VolumeDiscount>('/discounts', {
        method: 'POST',
        body: JSON.stringify(data),
      });
      await fetchVolumeDiscounts(1); // Re-fetch and go to page 1 after creation // MODIFIED: Added await and bypassCache is handled by apiClient for POST/PUT/DELETE
      return true;
    } catch (err) {
      //setError(err instanceof ApiError ? `Failed to create Volume Discount: ${err.message}` : 'An unknown error occurred during creation.');
      return false;
    }
  };

  const updateVolumeDiscount = async (id: number, data: VolumeDiscountUpdate): Promise<boolean> => {
    setError(null);
    try {
      await apiClient<VolumeDiscount>(`/discounts/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      });
      await fetchVolumeDiscounts(currentPage); // Re-fetch current page to show updated data // MODIFIED: Added await and bypassCache is handled by apiClient for POST/PUT/DELETE
      return true;
    } catch (err) {
      //setError(err instanceof ApiError ? `Failed to update Volume Discount: ${err.message}` : 'An unknown error occurred during update.');
      return false;
    }
  };

  const deleteVolumeDiscount = async (id: number): Promise<boolean> => {
    if (!window.confirm(`Are you sure you want to delete Volume Discount ID: ${id}?`)) return false;
    setError(null);
    try {
      await apiClient<void>(`/discounts/${id}`, { method: 'DELETE' });
      const pageToFetch = volumeDiscounts.length === 1 && currentPage > 1 ? currentPage - 1 : currentPage;
      await fetchVolumeDiscounts(pageToFetch); // Re-fetch, potentially moving back a page // MODIFIED: Added await and bypassCache is handled by apiClient for POST/PUT/DELETE
      return true;
    } catch (err) {
      ////setError(err instanceof ApiError ? `Failed to delete Volume Discount: ${err.message}` : 'An unknown error occurred during deletion.');
      return false;
    }
  };

  return {
    volumeDiscounts,
    loading,
    error,
    currentPage,
    totalPages,
    totalItems,
    fetchVolumeDiscounts,
    handlePageChange,
    createVolumeDiscount,
    updateVolumeDiscount,
    deleteVolumeDiscount,
  };
};